/**
 * Persistence helpers for the login experience (localStorage — persists across
 * refreshes, tabs and browser restarts):
 *
 * - `auth:last-method` — which method the user signed in with last, to skip the
 *   silent SSO probe for password users.
 * - `auth:manual-logout` — a sticky flag set ONLY when the user clicks "Log out".
 *   It suppresses silent SSO on every subsequent /login visit until the user
 *   explicitly clicks "Login with Microsoft" (or signs in via Microsoft). It is
 *   NOT set on inactivity timeout or cross-tab logout, and NOT cleared on refresh.
 * - `auth:sso-redirect-tried` — per-tab one-shot (sessionStorage) so the
 *   Safari/Firefox auto-redirect fallback runs at most once and a cancelled
 *   redirect doesn't trap the user in a loop.
 *
 * All accessors are wrapped in try/catch — storage can throw in private mode.
 */

const LAST_METHOD_KEY = 'auth:last-method'
const MANUAL_LOGOUT_KEY = 'auth:manual-logout'
const SSO_REDIRECT_TRIED_KEY = 'auth:sso-redirect-tried'

export type AuthMethod = 'password' | 'microsoft'

export const rememberAuthMethod = (method: AuthMethod): void => {
  try {
    localStorage.setItem(LAST_METHOD_KEY, method)
  } catch {
    // ignore — not critical
  }
}

export const getLastAuthMethod = (): AuthMethod | null => {
  try {
    return localStorage.getItem(LAST_METHOD_KEY) as AuthMethod | null
  } catch {
    return null
  }
}

/** Set the sticky "user logged out by hand" flag. Call only from the Log out button. */
export const markManualLogout = (): void => {
  try {
    localStorage.setItem(MANUAL_LOGOUT_KEY, '1')
  } catch {
    // ignore
  }
}

/** True while silent SSO should stay off because the user logged out manually. */
export const isManualLogout = (): boolean => {
  try {
    return localStorage.getItem(MANUAL_LOGOUT_KEY) === '1'
  } catch {
    return false
  }
}

/** Clear the flag — the user explicitly opted back into Microsoft sign-in. */
export const clearManualLogout = (): void => {
  try {
    localStorage.removeItem(MANUAL_LOGOUT_KEY)
  } catch {
    // ignore
  }
}

export const markSsoRedirectTried = (): void => {
  try {
    sessionStorage.setItem(SSO_REDIRECT_TRIED_KEY, '1')
  } catch {
    // ignore
  }
}

export const wasSsoRedirectTried = (): boolean => {
  try {
    return sessionStorage.getItem(SSO_REDIRECT_TRIED_KEY) === '1'
  } catch {
    return false
  }
}

export const clearSsoRedirectTried = (): void => {
  try {
    sessionStorage.removeItem(SSO_REDIRECT_TRIED_KEY)
  } catch {
    // ignore
  }
}
